import { getMetaCookies } from '@root/utils/cookies/cookies';
import mixpanel from 'mixpanel-browser';
import getConfig from 'next/config';
import { fetchApiTrackLogNewRelicCreate } from 'openapi/client/track/list-log-new-relic';
import { shouldDisableTrackingPersistance } from 'pages/_document';
import trackService from 'services/trackService';
import fetcher from 'utils/api';
import { v4 as uuidv4 } from 'uuid';

import { store } from '../store';
import { isElectron } from './electron';
import { hashString } from './utils';

const { publicRuntimeConfig } = getConfig();

const CIO_EVENTS = [
  'add_task',
  'show_feature_gate_modal',
  'extension_dropdown_open_recommended_clicked',
  'joined_community_waitlist',
  'click_share_modal_tab',
  'onboarding_modal_skip_install_clicked_restricted_extension',
  'viewer_first_login',
  'copilot_entry_point_click',
  'copilot_initial_prompt_submit',
  'copilot_browser_click_suggested',
  'opened_workflow_analyst'
];
const CAPI_TRACKED_ACTIONS_MAP = { shared_first_scribe: 'First Scribe Share' };
const NEW_RELIC_EVENTS = ['account_created', 'onboarding_completed'];
const NEW_RELIC_PATHS = ['/signup', '/workspace', '/welcome'];

mixpanel.init(publicRuntimeConfig.mixpanelProjectToken, {
  disable_persistence: shouldDisableTrackingPersistance(global?.window && global.window.location.pathname.split('/')[1]),
  api_host: publicRuntimeConfig.analyticsProxyUrl,
  ignore_dnt: true,
  error_reporter: (message, error) => {
    trackNewRelicLog('mixpanel_error', null, { message, error });
  }
});

const shouldTrack =
  process.env.NODE_ENV === 'production' &&
  (!global.window ||
    (global.window.location.origin &&
      !global.window.location.origin.includes('localhost') &&
      !global.window.location.origin.includes('chromatic.com')));

// Note(george): user-Uuid is a key used app wide so in order to properly set it,
// we need to match the behavior of useLocalStorage that's why encoding and decoding JSON is needed here
export function getUserUUID() {
  const storedValue = localStorage.getItem('user-Uuid');
  let userUUID = storedValue ? JSON.parse(storedValue) : null;

  if (!userUUID) {
    userUUID = uuidv4();
    localStorage.setItem('user-Uuid', JSON.stringify(userUUID));
  }
  return userUUID;
}

export const trackNewRelicLog = async (message, userId, attributes = {}, data = {}) => {
  const currentUserId = userId || getUserUUID();
  const eventFields = {
    user_id: currentUserId,
    attributes: { ...attributes, ...data },
    message
  };

  if (shouldTrack) {
    return fetchApiTrackLogNewRelicCreate({ body: eventFields, isAuthenticated: false });
  }

  // eslint-disable-next-line no-console
  console.log(`Would track a NewRelic log: ${message}: ${JSON.stringify(eventFields)} in production.`);

  return Promise.resolve();
};

export async function fireCAPIEvent(eventFields) {
  return fetcher(`track/capi/`, 'POST', true, eventFields);
}

export function getPersonalInfoParams(auth, account, billingDetails) {
  const email = auth?.data?.email || account?.data?.email;
  const firstName = auth?.data?.first_name || account?.data?.first_name;
  const lastName = auth?.data?.last_name || account?.data?.last_name;
  const userInfoParams = {};

  if (email) {
    const emailSha = hashString(email);

    userInfoParams.email_sha = emailSha;
  }
  if (firstName) {
    const firstNameSha = hashString(firstName);

    userInfoParams.first_name_sha = firstNameSha;
  }
  if (lastName) {
    const lastNameSha = hashString(lastName);

    userInfoParams.last_name_sha = lastNameSha;
  }
  // optionally we add address elements for enhanced conversions
  if (billingDetails && billingDetails.address) {
    userInfoParams.postal_code = billingDetails.address.postal_code;
    userInfoParams.country = billingDetails.address.country;
  }
  return userInfoParams;
}

/**
 *
 * @param {string} eventName
 * @param {Record<string, any>=} details - Custom properties to include with the event
 * @param {string=} eventCategory
 * @param {string=} eventAction
 */
export const trackEvent = (eventName, details = {}, eventCategory = null, eventAction = null) => {
  const { account } = store.getState();

  // This was necessary to handle details objects that "aren't extensible."
  const payload = {
    ...details,
    is_electron: isElectron(),
    super_organization_id: account?.data?.active_organization?.super_organization?.id,
    active_organization_id: account?.data?.active_organization?.id,
    current_plan_atm: account?.data?.active_organization?.super_organization?.plan?.name || 'free',
    current_pro_plan_type_atm: account?.data?.active_organization?.super_organization?.pro_plan_type,
    current_active_team_size: account?.data?.active_organization?.user_count
  };

  if (shouldTrack) {
    mixpanel.track(eventName, payload);

    const currentUserId = account?.data?.id;

    // GTM tracking, we are forwarding certain key events to 3rd party systems (Meta, google, tiktok, etc.)
    if (eventCategory && eventAction) {
      if (eventAction in CAPI_TRACKED_ACTIONS_MAP) {
        const eventId = uuidv4();
        const userInfoParams = getPersonalInfoParams(null, account);

        getMetaCookies()
          .then(({ fbc, fbp }) =>
            fireCAPIEvent({
              event_name: CAPI_TRACKED_ACTIONS_MAP[eventAction],
              event_id: eventId,
              external_id: currentUserId,
              ...userInfoParams,
              fbc,
              fbp
            })
          )
          .then(() => trackEvent('capi_tracking_successful', { event: 'signup', event_id: eventId, ...userInfoParams }))
          .catch(() => trackEvent('capi_tracking_failed', { event: 'signup', event_id: eventId, ...userInfoParams }));
        if (window?.dataLayer) {
          window.dataLayer.push({
            event: 'GTMevent',
            eventCategory,
            eventAction,
            eventLabel: eventName,
            userId: currentUserId,
            metaExternalId: currentUserId,
            metaEventId: eventId,
            ...userInfoParams
          });
        }
      } else if (window?.dataLayer) {
        window.dataLayer.push({
          event: 'GTMevent',
          eventCategory,
          eventAction,
          eventLabel: eventName,
          userId: currentUserId
        });
      }
    }

    // Customer IO tracking
    if (CIO_EVENTS.includes(eventName)) {
      trackService.trackCioEvent(eventName, details);
    }

    // New relic tracking, we are forwarding key events to new relic for better monitoring and alerting
    const eventPath = details?.path;

    if (NEW_RELIC_EVENTS.includes(eventName) || (eventPath && NEW_RELIC_PATHS.includes(eventPath))) {
      trackNewRelicLog(eventName, currentUserId, details);
    }
  } else {
    console.log(`Would track event: ${eventName}: ${JSON.stringify(payload)} in production.`);
  }
};

export const trackExperiment = (experimentName, variantValue, details = {}) => {
  if (variantValue !== undefined) {
    trackEvent('view_experiment', {
      experiment: experimentName,
      variant: String(variantValue),
      ...details
    });
  }
};

const windowUnloaded = () => {
  trackEvent('window_unloaded');
};

export const trackSession = (window) => {
  trackEvent('window_loaded');

  window.addEventListener('unload', windowUnloaded);

  return function () {
    window.removeEventListener('unload', windowUnloaded);
  };
};

export const trackSignup = (signupMethod, userId) => {
  if (shouldTrack) {
    const { account, auth } = store.getState();
    const currentUserId = account?.data?.id || userId;
    const userInfoParams = getPersonalInfoParams(auth, account);
    const eventId = uuidv4();

    if (window?.dataLayer) {
      window.dataLayer.push({
        event: 'GTMevent',
        eventCategory: 'account',
        eventAction: 'signup',
        eventLabel: signupMethod,
        userId: currentUserId,
        metaExternalId: currentUserId,
        metaEventId: eventId,
        ...userInfoParams
      });
    }

    getMetaCookies()
      .then(({ fbc, fbp }) =>
        fireCAPIEvent({ event_name: 'Lead', event_id: eventId, external_id: currentUserId, ...userInfoParams, fbc, fbp })
      )
      .then(() => trackEvent('capi_tracking_successful', { event: 'signup', event_id: eventId, ...userInfoParams }))
      .catch(() => trackEvent('capi_tracking_failed', { event: 'signup', event_id: eventId, ...userInfoParams }));
  } else {
    console.log('Would track signup');
  }
};

/**
 *
 * @param {Record<string, any>=} body
 */
export const trackAccountCreatedForGoogleSSO = (body) => {
  if (shouldTrack) {
    trackEvent('account_created', body);
  } else {
    console.log('Would track account created for google SSO');
  }
};

export const trackSignIn = (signInMethod, userId) => {
  if (shouldTrack) {
    if (window?.dataLayer) {
      const { account } = store.getState();

      window.dataLayer.push({
        event: 'GTMevent',
        eventCategory: 'account',
        eventAction: 'signin',
        eventLabel: signInMethod,
        userId: userId || account?.data?.id
      });
    }
  } else {
    console.log('Would track signin');
  }
};

export const trackFirstScribeCreation = (scribeId) => {
  if (shouldTrack) {
    trackEvent(
      'created_first_scribe',
      {
        file_id: scribeId
      },
      'action',
      'first_scribe_created'
    );
  } else {
    console.log('Would track created first scribe');
  }
};

export const trackChromeInstallToSignup = (variant) => {
  if (shouldTrack && window?.dataLayer) {
    trackEvent('chrome_install_to_signup', {
      variant
    });
    window.dataLayer.push({
      event: 'GTMevent',
      eventCategory: 'account',
      eventAction: 'extension_install',
      eventLabel: variant === 'treatment' ? 'chrome_install_to_signup_treatment' : 'chrome_install_to_signup_control'
    });
  }
};
